import { baseService } from "src/store/base.store";
import { executeQuery } from "src/store";
import { functionErrorsRepository } from "./repositories/function-errors.repository";
import type {
  FunctionErrorAggregate,
  FunctionErrorRow,
  FunctionErrorsFilter
} from "./types/function-error.entity";

const mapFunctionErrorRow = (
  item: FunctionErrorAggregate & { id: string }
): FunctionErrorRow => ({
  id: item.id,
  userId: item.userId ?? null,
  functionName: item.functionName ?? "",
  message: item.message ?? "",
  code: item.code,
  severity: item.severity ?? "",
  count: item.count ?? 0,
  firstSeenAt: item.firstSeenAt?.toDate?.()?.toISOString?.() ?? "",
  lastSeenAt: item.lastSeenAt?.toDate?.()?.toISOString?.() ?? ""
});

const buildWhere = (where: FunctionErrorsFilter = {}) => {
  const filters: any = {};

  if (where.userId) {
    filters.userId = where.userId;
  }

  if (where.functionName) {
    const functionName = where.functionName.trim();
    if (functionName) {
      filters.functionName = functionName;
    }
  }

  return filters;
};

export const functionErrorsService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getFunctionErrors: builder.query<
      FunctionErrorRow[],
      { where?: FunctionErrorsFilter; offset?: string; limit?: number }
    >({
      queryFn: async ({ where = {}, offset, limit }) => {
        const filters = buildWhere(where);
        const result = await executeQuery(() =>
          functionErrorsRepository.find({
            where: filters,
            offset,
            limit,
            orderBy: ["lastSeenAt", "desc"]
          } as any)
        );

        if ("error" in result) return { error: result.error } as any;
        return {
          data: (
            result.data as (FunctionErrorAggregate & { id: string })[]
          ).map(mapFunctionErrorRow)
        };
      },
      providesTags: ["function-errors"]
    }),

    countFunctionErrors: builder.query<
      number,
      { where?: FunctionErrorsFilter }
    >({
      queryFn: async ({ where = {} }) => {
        const filters = buildWhere(where);
        return executeQuery(() =>
          functionErrorsRepository.count(filters)
        ) as any;
      },
      providesTags: ["function-errors"]
    })
  })
});

export const { useGetFunctionErrorsQuery, useCountFunctionErrorsQuery } =
  functionErrorsService;
